import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import Paper from '@material-ui/core/Paper';
import Dialog from '@material-ui/core/Dialog';

class DashboardVideoModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loaded: false,
            isPlaying: false,
            playerKey: 0, // force fresh Wistia embed each close->reopen
        };

        this.modalRef = React.createRef();
        this.playerContainerRef = React.createRef();

        this.wistiaPlayer = null;
        this.unbinds = [];

        this.handleEsc = this.handleEsc.bind(this);
        this.trapTab = this.trapTab.bind(this);
        this.onPlayerKeyDown = this.onPlayerKeyDown.bind(this);
        this.onPlayerKeyUp = this.onPlayerKeyUp.bind(this);
        this.togglePlayPause = this.togglePlayPause.bind(this);
    }

    componentDidMount() {
        // Load Wistia API once
        if (!document.getElementById("wistia-external-e-v1")) {
            const s = document.createElement("script");
            s.id = "wistia-external-e-v1";
            s.src = "https://fast.wistia.com/assets/external/E-v1.js";
            s.async = true;
            document.body.appendChild(s);
        }
    }

    componentDidUpdate(prevProps) {
        const { show, videoId } = this.props;
        const justOpened = !prevProps.show && show;
        const justClosed = prevProps.show && !show;

        if (justOpened) {
            document.addEventListener("keydown", this.handleEsc);

            // Fresh onReady each open; playerKey ensures a new embed node
            if (typeof window !== "undefined") {
                window._wq = window._wq || [];
                window._wq.push({
                    id: videoId,
                    onReady: (video) => {
                        this.wistiaPlayer = video;

                        const unbindPlay = video.bind("play", () => this.setState({ isPlaying: true }));
                        const unbindPause = video.bind("pause", () => this.setState({ isPlaying: false }));
                        const unbindEnd = video.bind("end", () => this.setState({ isPlaying: false }));
                        this.unbinds = [unbindPlay, unbindPause, unbindEnd];

                        if (!this.state.loaded) this.setState({ loaded: true });
                    },
                });
            }

            // Focus the container so Enter/Space work immediately
            setTimeout(() => {
                if (this.playerContainerRef.current && typeof this.playerContainerRef.current.focus === "function") {
                    this.playerContainerRef.current.focus();
                }
            }, 50);
        } else if (justClosed) {
            document.removeEventListener("keydown", this.handleEsc);

            // Pause & unbind
            if (this.wistiaPlayer && typeof this.wistiaPlayer.pause === "function") {
                this.wistiaPlayer.pause();
            }
            if (this.unbinds && this.unbinds.length) {
                this.unbinds.forEach((u) => typeof u === "function" && u());
            }
            this.unbinds = [];
            this.wistiaPlayer = null;

            // Fresh embed next time; reset flags
            this.setState((s) => ({
                isPlaying: false,
                loaded: false,
                playerKey: s.playerKey + 1,
            }));

            // Return focus to opener if provided
            if (this.props.returnFocusEl && typeof this.props.returnFocusEl.focus === "function") {
                this.props.returnFocusEl.focus();
            }
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleEsc);
    }

    handleEsc(e) {
        if (e && (e.key === "Escape" || e.keyCode === 27)) {
            this.props.onClose && this.props.onClose();
        }
    }

    // keep tab focus inside dialog content
    trapTab(e) {
        if (!this.props.show) return;
        const isTab = e.key === "Tab" || e.keyCode === 9;
        if (!isTab) return;

        const container = this.modalRef.current;
        if (!container) return;

        const focusable = container.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
        } else {
            if (document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }

    togglePlayPause() {
        const p = this.wistiaPlayer;
        if (!p || typeof p.state !== "function") return;
        const state = p.state();
        if (state === "playing") {
            if (typeof p.pause === "function") p.pause();
        } else {
            if (typeof p.play === "function") p.play();
        }
    }

    // Only act when the event target IS the container (not Wistia controls)
    onPlayerKeyDown(e) {
        const isEnter = e.key === "Enter" || e.keyCode === 13;
        const isSpace = e.key === " " || e.key === "Spacebar" || e.keyCode === 32;

        const onContainer = e.currentTarget === e.target;
        if (!onContainer) return;

        if (isEnter) {
            e.preventDefault();
            this.togglePlayPause();
        } else if (isSpace) {
            e.preventDefault();
        }
    }

    onPlayerKeyUp(e) {
        const isSpace = e.key === " " || e.key === "Spacebar" || e.keyCode === 32;

        const onContainer = e.currentTarget === e.target;
        if (!onContainer) return;

        if (!isSpace) return;
        e.preventDefault();
        this.togglePlayPause();
    }

    render() {
        const { show, onClose, classes, videoId } = this.props;
        const { loaded, isPlaying, playerKey } = this.state;

        if (!show) return null;

        return (
            <Dialog open={show} onClose={onClose} aria-labelledby="dashboard-video-dialog">
                <Paper className={classes.root}>
                    <div
                        className={classes.modal}
                        id="dashboard-video-dialog"
                        role="dialog"
                        aria-modal="true"
                        ref={this.modalRef}
                        onKeyDown={this.trapTab}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            ref={this.playerContainerRef}
                            className={classes.playerWrap}
                            tabIndex={0}
                            role="button"
                            aria-label="Dashboard help video player. Press Space or Enter to play or pause."
                            aria-pressed={isPlaying}
                            onKeyDown={this.onPlayerKeyDown}
                            onKeyUp={this.onPlayerKeyUp}
                        >
                            {/* Wistia async embed target; key forces fresh mount */}
                            <div
                                key={"wistia-" + playerKey}
                                className={`wistia_embed wistia_async_${videoId} ${
                                    loaded ? classes.playerVisible : classes.playerHidden
                                }`}
                                style={{ width: "100%", height: "100%" }}
                                data-seo="true"
                                data-videofoam="false"
                                data-autoplay="false"
                                data-muted="false"
                            />
                        </div>
                    </div>
                </Paper>
            </Dialog>
        );
    }
}

DashboardVideoModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func,
    classes: PropTypes.object,
    videoId: PropTypes.string,
    returnFocusEl: PropTypes.object,
};

DashboardVideoModal.defaultProps = {
    videoId: "sgtvqidgd6",
};

const styles = {
    root: {
        position: "relative",
    },
    modal: {
        padding: 16,
        position: "fixed",
        width: 889,
        height: 500,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: "0 10px 35px rgba(0,0,0,0.35)",
        zIndex: 100,
        background: "white",
        borderRadius: 4,
        outline: "none",
    },
    playerWrap: {
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#000",
        overflow: "visible",
        borderRadius: 4,
        cursor: "default",
        "&:focus": {
            boxShadow: "0 0 0 3px rgba(21, 156, 228, 0.6)",
            outline: "none",
        },
    },
    playerHidden: {
        opacity: 0,
        transition: "opacity 200ms ease",
        width: "100%",
        height: "100%",
    },
    playerVisible: {
        opacity: 1,
        transition: "opacity 200ms ease",
        width: "100%",
        height: "100%",
    },
};

export default injectSheet(styles)(DashboardVideoModal);



// WEBPACK FOOTER //
// ./src/components/modals/DashboardVideoModal.js